import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api, type MeResponse } from "../lib/api";
import { setUserId, trackLogin, trackLogout } from "../lib/analytics";

import type { AuthBusiness, AuthUser, BusinessStatus, RegisterPayload, Role } from "../types/auth";

export type ImpersonationInfo = NonNullable<MeResponse["impersonation"]>;

export type ProfileGateInfo = {
  completion: number;
  nextStep: string | null;
};

type AuthSession = {
  token: string | null;
  user: AuthUser | null;
  business: AuthBusiness | null;
  lastLoginAt: string | null;
  sessionStartedAt: number | null;
  impersonation: ImpersonationInfo | null;
  profileGate: ProfileGateInfo | null;
};

type AuthContextValue = {
  token: string | null;
  user: AuthUser | null;
  business: AuthBusiness | null;
  lastLoginAt: string | null;
  sessionStartedAt: number | null;
  impersonation: ImpersonationInfo | null;
  profileGate: ProfileGateInfo | null;
  needsProfileCompletion: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  logout: () => void;
  refreshMe: (overrideToken?: string | null) => Promise<void>;
  startImpersonation: (targetUserId: string) => Promise<void>;
  endImpersonation: () => Promise<void>;
};

const TOKEN_STORAGE_KEY = "upLevoPos_auth_token_v1";

const AuthContext = createContext<AuthContextValue | null>(null);

function readTokenFromStorage() {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeTokenToStorage(token: string | null) {
  try {
    if (!token) localStorage.removeItem(TOKEN_STORAGE_KEY);
    else localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    // ignore storage errors (private mode, etc.)
  }
}

function applyMeToSession(me: MeResponse, token: string): AuthSession {
  return {
    token,
    user: me.user,
    business: me.business,
    lastLoginAt: me.lastLoginAt,
    sessionStartedAt: me.sessionStartedAt,
    impersonation: me.impersonation,
    profileGate: null,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<AuthSession>(() => ({
    token: readTokenFromStorage(),
    user: null,
    business: null,
    lastLoginAt: null,
    sessionStartedAt: null,
    impersonation: null,
    profileGate: null,
  }));
  const [isLoading, setIsLoading] = useState(true);
  const refreshInFlightRef = useRef<Promise<void> | null>(null);
  const refreshTokenRef = useRef<string | null>(null);

  // Keep GA4 user_id in sync with auth state (SPA journey tracking after login).
  useEffect(() => {
    setUserId(session.token && session.user ? session.user.id : null);
  }, [session.token, session.user]);

  const clearSession = useCallback(() => {
    setSession({
      token: null,
      user: null,
      business: null,
      lastLoginAt: null,
      sessionStartedAt: null,
      impersonation: null,
      profileGate: null,
    });
    writeTokenToStorage(null);
  }, []);

  const refreshMe = useCallback(async (overrideToken?: string | null) => {
    const token = overrideToken ?? readTokenFromStorage();
    if (!token) {
      clearSession();
      return;
    }

    // Deduplicate concurrent refresh calls for the same token (prevents double `/me` requests).
    if (refreshInFlightRef.current && refreshTokenRef.current === token) {
      return refreshInFlightRef.current;
    }

    const run = (async () => {
    try {
      const me = await api.auth.me({ token });
      let next = applyMeToSession(me, token);

      // Enrich business details for owners (e.g. rejectionReason).
      if (me.user.role === "business_owner") {
        const my = await api.business.getMyBusiness({ token });
        next = {
          ...next,
          business: my.business,
        };
      }

      setSession(next);
      writeTokenToStorage(token);
    } catch (err) {
      const apiErr = err as {
        code?: string;
        data?: {
          token?: string;
          user?: AuthUser;
          business?: AuthBusiness | null;
          completion?: number;
          next_step?: string;
        };
      };
      if (apiErr?.code === "BUSINESS_NOT_VERIFIED" && apiErr.data?.token && apiErr.data?.user) {
        setSession({
          token: apiErr.data.token,
          user: apiErr.data.user,
          business: apiErr.data.business ?? null,
          lastLoginAt: null,
          sessionStartedAt: null,
          impersonation: null,
          profileGate: null,
        });
        writeTokenToStorage(apiErr.data.token);
        return;
      }
      if (apiErr?.code === "PROFILE_INCOMPLETE" && apiErr.data?.token && apiErr.data?.user) {
        setSession({
          token: apiErr.data.token,
          user: apiErr.data.user,
          business: apiErr.data.business ?? null,
          lastLoginAt: null,
          sessionStartedAt: null,
          impersonation: null,
          profileGate: {
            completion: apiErr.data.completion ?? 0,
            nextStep: apiErr.data.next_step ?? null,
          },
        });
        writeTokenToStorage(apiErr.data.token);
        return;
      }
      clearSession();
      throw err;
    }
    })();

    refreshTokenRef.current = token;
    refreshInFlightRef.current = run.finally(() => {
      if (refreshTokenRef.current === token) {
        refreshTokenRef.current = null;
        refreshInFlightRef.current = null;
      }
    });

    return refreshInFlightRef.current;
  }, [clearSession]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refreshMe();
      } catch {
        // ignore bootstrap errors; guards will handle redirects
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshMe]);

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        const res = await api.auth.login({ email, password });
        writeTokenToStorage(res.token);
        await refreshMe(res.token);
        trackLogin("password");
      } catch (err) {
        const apiErr = err as {
          code?: string;
          data?: {
            token?: string;
            user?: AuthUser;
            business?: AuthBusiness | null;
            completion?: number;
            next_step?: string;
          };
        };
        if (apiErr?.code === "BUSINESS_NOT_VERIFIED" && apiErr.data?.token && apiErr.data?.user) {
          setSession({
            token: apiErr.data.token,
            user: apiErr.data.user,
            business: apiErr.data.business ?? null,
            lastLoginAt: null,
            sessionStartedAt: null,
            impersonation: null,
            profileGate: null,
          });
          writeTokenToStorage(apiErr.data.token);
        }
        if (apiErr?.code === "PROFILE_INCOMPLETE" && apiErr.data?.token && apiErr.data?.user) {
          setSession({
            token: apiErr.data.token,
            user: apiErr.data.user,
            business: apiErr.data.business ?? null,
            lastLoginAt: null,
            sessionStartedAt: null,
            impersonation: null,
            profileGate: {
              completion: apiErr.data.completion ?? 0,
              nextStep: apiErr.data.next_step ?? null,
            },
          });
          writeTokenToStorage(apiErr.data.token);
        }
        throw err;
      }
    },
    [refreshMe],
  );

  const register = useCallback(
    async (payload: RegisterPayload) => {
      const res = await api.auth.register(payload);
      writeTokenToStorage(res.token);
      await refreshMe(res.token);
    },
    [refreshMe],
  );

  const logout = useCallback(() => {
    const token = readTokenFromStorage();
    if (!token) {
      clearSession();
      trackLogout();
      return;
    }

    // Logout should invalidate the JWT server-side, then clear local state.
    void (async () => {
      try {
        await api.auth.logout({ token });
      } finally {
        clearSession();
        trackLogout();
      }
    })();
  }, [clearSession]);

  const startImpersonation = useCallback(async (targetUserId: string) => {
    const token = readTokenFromStorage();
    if (!token) return;
    const me = await api.admin.startImpersonation({ token, targetUserId });
    setSession(applyMeToSession(me, me.token));
    writeTokenToStorage(me.token);
  }, []);

  const endImpersonation = useCallback(async () => {
    const token = readTokenFromStorage();
    if (!token) return;
    const me = await api.admin.endImpersonation({ token });
    setSession(applyMeToSession(me, me.token));
    writeTokenToStorage(me.token);
  }, []);

  const needsProfileCompletion = Boolean(
    session.user?.role === "business_owner" && session.profileGate !== null,
  );

  const value: AuthContextValue = useMemo(
    () => ({
      token: session.token,
      user: session.user,
      business: session.business,
      lastLoginAt: session.lastLoginAt,
      sessionStartedAt: session.sessionStartedAt,
      impersonation: session.impersonation,
      profileGate: session.profileGate,
      needsProfileCompletion,
      isLoading,
      isAuthenticated: Boolean(session.token && session.user),
      login,
      register,
      logout,
      refreshMe,
      startImpersonation,
      endImpersonation,
    }),
    [
      endImpersonation,
      isLoading,
      login,
      refreshMe,
      register,
      session.business,
      session.impersonation,
      session.lastLoginAt,
      session.profileGate,
      session.sessionStartedAt,
      session.token,
      session.user,
      needsProfileCompletion,
      startImpersonation,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
